import axios from 'axios';
import { ModalContext } from 'app/app';
import { IconedButton } from 'components/IconedButton';
import ResponsiveTable from 'components/ResponsiveTable';
import {
  Animate,
  Grid,
  ViewContext,
} from 'components/lib';
import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DateUtils from 'helpers/dateUtils';
import InputUtils from 'helpers/inputUtils';
import NumberUtils from 'helpers/numberUtils';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { quoteColumns } from './columns';
import { Edit, Eye, Trash2 } from 'feather-icons-react/build/IconComponents';
import { FieldInput } from 'components/fieldInput';
import { getLatestSubmissionNumber } from 'helpers/submission';

export function Quote() {
  const navigate = useNavigate();
  const { setModal } = useContext(ModalContext);
  const context = useContext(ViewContext);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [agencyFilter, setAgencyFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const dateUtils = new DateUtils();
  const numberUtils = new NumberUtils();
  const inputUtils = new InputUtils();
  const debounceTimeout = useRef(null);
  const cancelTokenRef = useRef(null);

  async function fetchFilterOptions() {
    try {
      const [agenciesResponse, companiesResponse] = await Promise.all([
        axios.get('/api/account/all/minimal'),
        axios.get('/api/company/all')
      ]);

      setAgenciesOptions(
        agenciesResponse.data.data.map((agency) => {
          return {
            label: agency.name,
            value: agency.id,
          };
        })
      );

      setCompaniesOptions(
        companiesResponse.data.data.map((company) => {
          return {
            label: inputUtils
              .capitalizeFirstLetter(company.name)
              .replace('Llc', 'LLC'),
            value: company.id,
          };
        })
      );
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      context.notification.show(
        'Failed to load filter options',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  }

  const debounceSearch = useCallback((searchValue) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedFilter(searchValue);

      if (searchValue) {
        setPagination(prev => ({
          ...prev,
          pageIndex: 0
        }));
      }
    }, 500);
  }, []);

  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value);
    debounceSearch(value);
  };

  async function listQuotes() {
    try {
      setLoading(true);

      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Operation canceled due to new request.');
      }

      cancelTokenRef.current = axios.CancelToken.source();

      const companyFilterStr = Array.isArray(companyFilter)
        ? companyFilter.join(',')
        : companyFilter;
      const agencyFilterStr = Array.isArray(agencyFilter)
        ? agencyFilter.join(',')
        : agencyFilter;
      const statusFilterStr = Array.isArray(statusFilter)
        ? statusFilter.join(',')
        : statusFilter;

      const params = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedFilter || undefined,
        companyFilter: companyFilterStr,
        agencyFilter: agencyFilterStr,
        statusFilter: statusFilterStr
      };

      if (sorting.length > 0) {
        params.sortBy = sorting[0].id;
        params.sortOrder = sorting[0].desc ? 'desc' : 'asc';
      }

      const res = await axios.get('/api/quote/admin', {
        params,
        cancelToken: cancelTokenRef.current.token
      });

      const quotesData = res.data.data.data.quotes.map((value) => {
        return {
          id: value.id,
          company_id: value.company_id,
          submission_number: getLatestSubmissionNumber(value),
          dot_number: value.dot_number,
          company: inputUtils
            .capitalizeFirstLetter(value.company_name)
            .replaceAll('Llc', 'LLC'),
          carrier: Array.isArray(value.carrier)
            ? value.carrier.join(', ')
            : (value.carrier || 'N/A'),
          premium: value.premium
            ? numberUtils.formatToCurrency(value.premium)
            : numberUtils.formatToCurrency(0),
          "Agency Name": value.account_name ? (
            <a
              href={`/admin/agency/${value.account_id}`}
              target="_blank"
              rel="noreferrer"
              style={{
                color: 'blue',
                textDecoration: 'underline',
              }}
            >
              {value.account_name}
            </a>
          ) : (
            'N/A'
          ),
          Drivers: value.number_of_drivers || 0,
          Vehicles: value.number_of_vehicles || 0,
          Trailers: value.number_of_trailers || 0,
          "Created At": dateUtils.timestamp(value.createdAt),
          status: value.status === 'bound' ? 'active' : value.status.replaceAll('_', ' '),
          quote: value,
        };
      });

      setData(quotesData);
      setPagination({
        ...pagination,
        pageIndex: res.data.data.page - 1,
        totalItems: res.data.data.rows,
        totalPages: res.data.data.pages_count,
      });

      setLoading(false);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoading(false);
        context.notification.show(
          'Failed to load table data',
          'error',
          true,
          'toast',
          'x-circle'
        );
      }
    }
  }

  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedFilter,
    sorting,
    agencyFilter,
    companyFilter,
    statusFilter
  ]);

  const handleViewQuote = (row) => {
    const reviewUrl = "/admin/coverwhale/quote/review";
    navigate(reviewUrl, {
      state: {
        data: row.quote,
      },
    });
  };

  const handleEditQuote = (row) => {
    navigate('/admin/quote/form', {
      state: {
        data: {
          company_id: row.company_id,
          quote_id: row.id,
        },
      },
    });
  };

  const handleDeleteQuote = (quote) => {
    setModal({
      name: 'confirmation',
      data: {
        title: `Delete quote`,
        description: `Do you really want to delete this quote?`,
      },
      action: async () => {
        setModal('');
        setLoading(true);

        try {
          await axios.delete(`/api/quote/${quote.id}`);
          listQuotes();
        } catch (error) {
          setLoading(false);
          context.notification.show(
            'Failed to delete quote',
            'error',
            true,
            'toast',
            'x-circle'
          );
        }
      },
    });
  };

  const columnsWithActions = [
    ...quoteColumns,
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewQuote(row.original)}
            className="p-1 rounded hover:bg-gray-200"
            title="Review this quote"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleEditQuote(row.original)}
            className="p-1 rounded hover:bg-gray-200"
            title="Edit this quote"
          >
            <Edit size={16} color="#637381" />
          </button>
          <button
            onClick={() => handleDeleteQuote(row.original)}
            className="p-1 rounded hover:bg-gray-200"
            title="Delete quote"
          >
            <Trash2 size={16} color="#637381" />
          </button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns: columnsWithActions,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      rowSelection,
      pagination,
    },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    // Pagination
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    // Column
    onColumnFiltersChange: setColumnFilters,
    // Row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    // Sort and Filter
    onSortingChange: setSorting,
    onGlobalFilterChange: handleGlobalFilterChange,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    pageCount: pagination.totalPages,
    manualSorting: true,
    manualFiltering: true,
  });

  const filterComponents = (
    <div className="p-4 space-y-4">
      <div>
        <FieldInput
          id="agency_filter"
          type="multi_select"
          label="Filter by agency"
          isDisabled={loading}
          value={selectedAgencies}
          handleChange={(agency) => {
            const agencyValues = agency.map(a => a.value);
            setAgencyFilter(agencyValues);
            setSelectedAgencies(agency);
          }}
          options={agenciesOptions}
        />
      </div>
      <div>
        <FieldInput
          id="company_filter"
          type="multi_select"
          label="Filter by company"
          isDisabled={loading}
          value={selectedCompanies}
          handleChange={(company) => {
            const companyValues = company.map(c => c.value);
            setCompanyFilter(companyValues);
            setSelectedCompanies(company);
          }}
          options={companiesOptions}
        />
      </div>
      <div>
        <FieldInput
          id="status_filter"
          type="multi_select"
          label="Filter by status"
          isDisabled={loading}
          value={selectedStatuses}
          handleChange={(status) => {
            const statusValues = status.map(s => s.value);
            setStatusFilter(statusValues);
            setSelectedStatuses(status);
          }}
          options={[
            { value: '', label: 'All' },
            { value: 'bound', label: 'Active' },
            { value: "inactive", label: "Inactive" },
            { value: 'created', label: 'Created' },
            { value: 'submitted', label: 'Submitted' },
            { value: 'declined', label: 'Declined' },
            {
              value: 'requested_bind',
              label: 'Requested Bind',
            },
          ]}
        />
      </div>
    </div>
  );

  return (
    <Animate type="pop">
      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <IconedButton
            size="160px"
            color="#d32425"
            textColor="white"
            icon="file-text"
            text="Generate PDF"
            marginRight
            action={async () => {
              setModal({
                name: 'quotepdf_modal',
              });
            }}
          />
          <IconedButton
            size="240px"
            color="#d32425"
            textColor="white"
            icon="download-cloud"
            text="Unique Quotes Per Month"
            marginRight
            loading={false}
            action={async () => {
              const reportResponse = await axios.get(
                '/api/account/report/unique'
              );
              window.open(reportResponse.data.link);
            }}
          />
          <IconedButton
            size="210px"
            color="#d32425"
            textColor="white"
            icon="download-cloud"
            text="Agency Quote Report"
            marginRight
            loading={false}
            action={async () => {
              const reportResponse = await axios.get(
                '/api/account/report'
              );
              window.open(reportResponse.data.link);
            }}
          />
        </div>
      </div>
      <Grid cols="1">
        <ResponsiveTable
          table={table}
          globalFilter={globalFilter}
          setGlobalFilter={handleGlobalFilterChange}
          loading={loading}
          hasFilter={true}
          hasFooter={true}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          filters={filterComponents}
        />
      </Grid>
    </Animate>
  );
}
